import { useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import VoyageTempsModal from "@/components/game/VoyageTempsModal";
import {
  X,
  Lightbulb,
  Check,
  ScrollText,
  Sparkles,
  Search,
  Calculator,
  Eye,
} from "lucide-react";

/* -----------------------------------------------------------
   EnigmeModal — Écran plein sur mobile, modal centré desktop
   Types supportés : TEXTE · QCM · CALCUL · OBSERVATION
   Indices 3 niveaux : 1 offert, 2 (-25pts), 3 (-50pts)

   ⚠️ SÉCURITÉ : la validation est faite CÔTÉ SERVEUR via
   /api/sessions/by-code/{code}/valider-enigme. La réponse et le
   code cadenas ne sont pas transmis au client tant que l'énigme
   n'est pas résolue.
   ----------------------------------------------------------- */

/** Son de déverrouillage — Web Audio API pure, aucun asset externe */
function playUnlockSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const t0 = ctx.currentTime;
    const click = (t, freq, vol) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.4, t + 0.04);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      osc.connect(g).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.05);
    };
    click(t0, 900, 0.18);
    click(t0 + 0.07, 700, 0.22);
    click(t0 + 0.15, 1100, 0.28);
    const clunk = ctx.createOscillator();
    const cg = ctx.createGain();
    clunk.type = "sine";
    clunk.frequency.setValueAtTime(180, t0 + 0.3);
    clunk.frequency.exponentialRampToValueAtTime(60, t0 + 0.6);
    cg.gain.setValueAtTime(0.4, t0 + 0.3);
    cg.gain.exponentialRampToValueAtTime(0.001, t0 + 0.7);
    clunk.connect(cg).connect(ctx.destination);
    clunk.start(t0 + 0.3);
    clunk.stop(t0 + 0.7);
  } catch {
    /* pas de son sur ce navigateur */
  }
}

const TYPE_ICONS = {
  TEXTE: ScrollText,
  QCM: Search,
  CALCUL: Calculator,
  OBSERVATION: Eye,
};

const MALUS_INDICE_2 = 25;
const MALUS_INDICE_3 = 50;

export default function EnigmeModal({ enigme, etape, session, onClose, onSolved }) {
  const type = (enigme.type || "TEXTE").toUpperCase();

  const [reponse, setReponse] = useState("");
  const [tries, setTries] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reveal2, setReveal2] = useState(false);
  const [reveal3, setReveal3] = useState(false);
  const [phase, setPhase] = useState("question"); // "question" | "unlocked"
  // Codes/explication renvoyés par le serveur APRÈS validation réussie
  const [reveal, setReveal] = useState(null); // {codeCadenas, explication, ptsGagnes}

  // Routing spécial : Voyage dans le temps → sous-modal dédié
  if (type === "VOYAGE_TEMPS") {
    return (
      <VoyageTempsModal
        enigme={enigme}
        etape={etape}
        session={session}
        onClose={onClose}
        onSolved={onSolved}
      />
    );
  }

  const Icon = TYPE_ICONS[type] || ScrollText;

  async function askIndice(niveau) {
    try {
      await gameApi.indiceUtilise(session.codeAcces, {
        niveau,
        numeroCadenas: enigme.numeroCadenas,
      });
    } catch {
      /* silent — front continue même si tracking échoue */
    }
    if (niveau === 2) setReveal2(true);
    if (niveau === 3) setReveal3(true);
  }

  async function handleValider(overrideReponse) {
    const answer = (overrideReponse ?? reponse).toString().trim();
    if (!answer || submitting) return;
    setSubmitting(true);
    setFeedback(null);

    try {
      const r = await gameApi.validerEnigme(session.codeAcces, {
        numeroCadenas: enigme.numeroCadenas,
        answer,
        reveal2,
        reveal3,
        tries,
        tempsS: 0,
      });

      if (r.correct) {
        setReveal({
          codeCadenas: r.codeCadenas,
          explication: r.explication,
          ptsGagnes:   r.ptsGagnes || 0,
        });
        setFeedback({
          ok: true,
          msg: r.dejaOuvert
            ? "Cadenas déjà ouvert — voici le code."
            : `Bonne réponse · +${r.ptsGagnes} points`,
          pts: r.ptsGagnes || 0,
        });
        playUnlockSound();
        setTimeout(() => setPhase("unlocked"), 400);
      } else {
        setTries((t) => t + 1);
        setFeedback({
          ok: false,
          msg:
            tries === 0
              ? "Ce n'est pas encore ça. Réessayez, ou demandez un indice."
              : reveal3
              ? "Toujours pas. Regardez de nouveau les indices."
              : reveal2
                ? "Encore raté. Un troisième indice est disponible."
                : "Raté. Un indice supplémentaire est disponible.",
        });
      }
    } catch (err) {
      setFeedback({
        ok:  false,
        msg: err.response?.data?.detail || "Erreur de validation",
      });
      toast.error("Erreur de validation");
    }
    setSubmitting(false);
  }

  /* ═══════════ PHASE 2 : Cadenas déverrouillé ═══════════ */
  if (phase === "unlocked") {
    return (
      <div
        data-testid="enigme-modal-unlocked"
        className="fixed inset-0 z-[100] bg-noir/95 backdrop-blur-md flex items-center justify-center p-4 overflow-auto"
      >
        <div className="w-full max-w-md bg-gradient-to-b from-vert-900/60 to-noir border border-or/40 rounded-sm p-6 md:p-8 text-center shadow-[0_20px_60px_-10px_rgba(200,146,42,0.4)]">
          <div className="mx-auto w-20 h-20 rounded-full border-2 border-or bg-noir flex items-center justify-center mb-5 animate-glow-pulse">
            <Check className="w-10 h-10 text-or" strokeWidth={2.5} />
          </div>

          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
            Cadenas #{enigme.numeroCadenas} · Ouvert
          </div>
          <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight">
            <span className="italic text-or">Déverrouillé.</span>
          </h2>

          <div className="mt-6 p-5 bg-noir border border-or/30 rounded-sm">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 mb-3">
              Code à composer sur le cadenas
            </div>
            <div
              data-testid="revealed-code"
              className="font-mono font-bold text-5xl text-or stat-graved tracking-[0.35em]"
            >
              {reveal?.codeCadenas || enigme.codeCadenas}
            </div>
          </div>

          {(reveal?.explication || enigme.explication) && (
            <div className="mt-5 p-4 bg-vert-900/40 border-l-2 border-vert-50 text-left rounded-r-sm">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-vert-50" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-vert-50">
                  Le saviez-vous&nbsp;?
                </span>
              </div>
              <p className="text-sm text-parchemin/90 leading-relaxed italic font-display">
                {reveal?.explication || enigme.explication}
              </p>
            </div>
          )}

          <button
            onClick={onSolved}
            data-testid="continue-btn"
            className="mt-6 w-full py-3.5 bg-or hover:bg-or-100 text-noir font-medium tracking-wide rounded-sm transition shadow-[0_10px_30px_-10px_rgba(200,146,42,0.5)]"
          >
            Poursuivre l&apos;aventure →
          </button>
        </div>
      </div>
    );
  }

  /* ═══════════ PHASE 1 : Résoudre l'énigme ═══════════ */
  const amorce = enigme.indice1 || "";

  return (
    <div
      data-testid="enigme-modal"
      className="fixed inset-0 z-[100] bg-noir/95 backdrop-blur-md flex items-start md:items-center justify-center p-0 md:p-4 overflow-auto"
    >
      <div className="w-full max-w-2xl min-h-full md:min-h-0 md:max-h-[92vh] bg-noir-100 border border-or/25 md:rounded-sm md:overflow-hidden flex flex-col">
        {/* Header sticky */}
        <div className="flex items-start justify-between gap-3 p-5 md:p-6 border-b border-or/15 bg-noir-100/95 backdrop-blur">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-sm bg-or/10 border border-or/40 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-or" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
                Étape {etape.ordre} · Cadenas #{enigme.numeroCadenas} · {type}
              </div>
              <h2 className="mt-1 font-display text-xl md:text-2xl text-parchemin leading-tight">
                {enigme.resumeIdee || etape.nom}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="modal-close-btn"
            className="p-2 text-parchemin/50 hover:text-parchemin transition"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-5">
          {/* Amorce (indice 1 offert) */}
          {amorce && (
            <div
              data-testid="indice-niveau-1"
              className="flex items-start gap-3 p-4 bg-vert-900/40 border-l-2 border-vert-50 rounded-r-sm"
            >
              <Lightbulb className="w-4 h-4 text-vert-50 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-vert-50 mb-1">
                  Indice · Niveau 1 (offert)
                </div>
                <p className="text-sm text-parchemin/90 leading-relaxed italic font-display">
                  {amorce}
                </p>
              </div>
            </div>
          )}

          {/* Question */}
          <div
            data-testid="question-text"
            className="p-5 bg-noir-50/60 border border-or/20 rounded-sm text-parchemin"
          >
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 mb-3">
              L&apos;énigme
            </div>
            <p className="text-base md:text-lg leading-relaxed">{enigme.question}</p>
          </div>

          {/* Input selon type */}
          {type === "QCM" && Array.isArray(enigme.optionsQCM) ? (
            <div className="space-y-2" data-testid="qcm-choices">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                Choisissez la bonne réponse
              </div>
              {enigme.optionsQCM.map((opt, i) => {
                const selected = reponse === opt;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReponse(opt)}
                    data-testid={`qcm-opt-${i}`}
                    className={`w-full text-left flex items-center gap-3 p-4 rounded-sm border transition ${
                      selected
                        ? "border-or bg-or/10 text-parchemin"
                        : "border-or/15 bg-noir-50 text-parchemin/80 hover:border-or/50"
                    }`}
                  >
                    <span
                      className={`w-8 h-8 rounded-full font-mono text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                        selected
                          ? "bg-or text-noir"
                          : "border border-or/30 text-or/70"
                      }`}
                    >
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-sm md:text-base">{opt}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                {type === "CALCUL" ? "Résultat du calcul" : "Votre réponse"}
              </div>
              <input
                data-testid="reponse-input"
                type={type === "CALCUL" ? "number" : "text"}
                inputMode={type === "CALCUL" ? "numeric" : "text"}
                value={reponse}
                autoFocus
                onChange={(e) => setReponse(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !submitting) handleValider();
                }}
                placeholder={type === "CALCUL" ? "42" : "Tapez votre réponse…"}
                className={`w-full px-4 py-4 rounded-sm bg-noir border-2 border-or/25 text-parchemin outline-none focus:border-or transition ${
                  type === "CALCUL"
                    ? "font-mono text-2xl font-bold text-center tracking-[0.15em]"
                    : "text-lg"
                }`}
              />
              <div className="text-[11px] font-mono text-parchemin/40">
                Astuce : accents et casse ignorés.
              </div>
            </div>
          )}

          {/* Feedback */}
          {feedback && (
            <div
              data-testid="enigme-feedback"
              className={`p-3 rounded-sm border text-sm font-medium text-center ${
                feedback.ok
                  ? "bg-vert-900/50 border-vert-50/50 text-vert-50"
                  : "bg-oxyde/10 border-oxyde/40 text-oxyde"
              }`}
            >
              {feedback.msg}
            </div>
          )}

          {/* Indices niveau 2 & 3 */}
          <div className="space-y-2">
            {reveal2 && enigme.indice2 && (
              <div
                data-testid="indice-niveau-2"
                className="flex items-start gap-3 p-4 bg-or/5 border-l-2 border-or rounded-r-sm"
              >
                <Lightbulb className="w-4 h-4 text-or mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or mb-1">
                    Indice · Niveau 2
                  </div>
                  <p className="text-sm text-parchemin/85 leading-relaxed">
                    {enigme.indice2}
                  </p>
                </div>
              </div>
            )}
            {reveal3 && enigme.indice3 && (
              <div
                data-testid="indice-niveau-3"
                className="flex items-start gap-3 p-4 bg-oxyde/10 border-l-2 border-oxyde rounded-r-sm"
              >
                <Lightbulb className="w-4 h-4 text-oxyde mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-oxyde mb-1">
                    Indice · Niveau 3
                  </div>
                  <p className="text-sm text-parchemin/85 leading-relaxed">
                    {enigme.indice3}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {enigme.indice2 && !reveal2 && (
                <button
                  onClick={() => askIndice(2)}
                  data-testid="btn-indice-2"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-or/40 bg-or/5 text-or text-xs font-mono uppercase tracking-widest hover:bg-or/10 transition"
                >
                  <Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Indice 2 (−{MALUS_INDICE_2}&nbsp;pts)
                </button>
              )}
              {enigme.indice3 && !reveal3 && reveal2 && (
                <button
                  onClick={() => askIndice(3)}
                  data-testid="btn-indice-3"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-oxyde/50 bg-oxyde/5 text-oxyde text-xs font-mono uppercase tracking-widest hover:bg-oxyde/10 transition"
                >
                  <Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Indice 3 (−{MALUS_INDICE_3}&nbsp;pts)
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Actions bas fixes */}
        <div className="p-4 md:p-5 border-t border-or/15 bg-noir-100/95 backdrop-blur flex gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="back-btn"
            className="px-4 py-3 rounded-sm border border-parchemin/25 text-parchemin/70 hover:text-parchemin hover:border-parchemin/50 text-sm transition"
          >
            Retour
          </button>
          <button
            onClick={() => handleValider()}
            disabled={submitting || !reponse.toString().trim()}
            data-testid="valider-btn"
            className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-sm font-medium tracking-wide text-sm transition ${
              reponse.toString().trim() && !submitting
                ? "bg-or hover:bg-or-100 text-noir shadow-[0_10px_30px_-10px_rgba(200,146,42,0.5)]"
                : "bg-or/30 text-noir/40 cursor-not-allowed"
            }`}
          >
            {submitting ? "Vérification…" : "Vérifier ma réponse"}
          </button>
        </div>
      </div>
    </div>
  );
}
